/**
 * REST provider — our own Node + Express + MySQL backend.
 *
 * The third implementation of the contracts in ../contracts, alongside
 * firebase/ and mock/. Nothing above this layer can tell which one is live:
 * the methods take the same arguments, return the same shapes, and the
 * subscriptions hand back the same unsubscribe function.
 *
 * ---------------------------------------------------------------------------
 * HOW SUBSCRIPTIONS WORK HERE
 *
 * Firestore's onSnapshot gives both the first read and every later change
 * from one call. REST and a WebSocket are two halves of that, so every
 * subscribe() below does the same two things:
 *
 *   1. fetch the current state over HTTP and emit it immediately
 *   2. listen on the socket topic and re-emit as changes arrive
 *
 * Where a change carries enough detail the cache is patched in place; where
 * it does not, the list is refetched. Patching matters for messages — a chat
 * with a thousand messages must not refetch all of them because one arrived.
 *
 * ---------------------------------------------------------------------------
 * WHAT THE SERVER DOES NOT IMPLEMENT YET
 *
 * Everything but calls: auth, chats, messages, presence, contacts, media,
 * status and channels are all live against real routes. calls has no routes
 * yet, so it still throws — with a message naming the missing route rather
 * than something vague. See zivapp-server/src/routes/ for what exists.
 */

import { SERVICE_NAMES, REQUIRED_METHODS } from '../contracts/index.js'
import { ZivError, ErrorCode, notImplemented } from '../errors.js'
import {
  toUser, toChat, toMessage, toStatus, toChannel, toChannelPost, toCommunity
} from '../../models/index.js'
import { get, post, put, patch, del, setAccessToken, getAccessToken, restoreAccessToken } from './httpClient.js'
import { subscribe as onTopic, connect, disconnect, send } from './socketClient.js'
import { getConfig } from '../../config.js'

// ---------------------------------------------------------------------------
// auth
// ---------------------------------------------------------------------------

let currentUser = null
const authListeners = new Set()

function emitAuth () {
  for (const listener of authListeners) listener(currentUser)
}

/**
 * Restore a saved session once, the first time anything asks who is here.
 *
 * Every authenticated read awaits this. Without it a reload is a race: the
 * token lives in storage and is read asynchronously, so a subscription
 * created during the first render fetches before the Authorization header
 * exists, takes a 401, and never retries. The chat list happened to win
 * that race and the conversation did not, which is how it showed a list of
 * chats above a conversation stuck on "Loading messages…" forever.
 */
let restoring = null
function restoreSession () {
  restoring ??= (async () => {
    await restoreAccessToken()
    if (!getAccessToken()) return null
    try {
      const row = await get('/api/auth/me')
      currentUser = toUser(row.id, row)
      connect()
    } catch (error) {
      /*
       * ONLY a rejected token signs you out.
       *
       * This used to catch everything and throw the token away, so any
       * failure to reach the server — it restarting, a tunnel dropping,
       * a train going into a cutting — permanently logged the person
       * out. Reported as "the app closes after 30 minutes and tells me
       * Get started"; it was not a timer, it was whichever reload
       * happened to land while the server was down.
       *
       * The token is good for thirty days. A network error says nothing
       * about whether it is still valid, so the session is kept and the
       * next read retries. Only 401 and 403 mean the server looked at
       * the token and refused it.
       */
      const rejected =
        error?.code === ErrorCode.UNAUTHENTICATED ||
        error?.code === ErrorCode.PERMISSION

      if (rejected) {
        setAccessToken(null)
        currentUser = null
      } else {
        console.warn('[ZIvapp] could not reach the server; keeping the session.', error)
        // Let the next call try again rather than caching this failure
        // as the answer for the rest of the page's life.
        restoring = null
      }
    }
    emitAuth()
    return currentUser
  })()
  return restoring
}

export const auth = {
  async requestCode (phone) {
    const result = await post('/api/auth/request-code', { phone })
    // The server does not mint a verification id — the phone number is the
    // handle. Returned under the same key so callers need not care which
    // backend they are on.
    //
    // devCode is present only while the server has no SMS provider and is
    // running in development; it vanishes the moment one is configured.
    // The verify screen shows it so the app can be used without a
    // terminal open beside it.
    return {
      verificationId: phone,
      expiresInSeconds: result?.expiresInSeconds,
      devCode: result?.devCode
    }
  },

  async confirmCode (verificationId, code) {
    const { token, user } = await post('/api/auth/confirm-code', {
      phone: verificationId,
      code
    })
    setAccessToken(token)
    currentUser = toUser(user.id, user)
    connect()
    emitAuth()
    return currentUser
  },

  async signOut () {
    setAccessToken(null)
    currentUser = null
    disconnect()
    emitAuth()
  },

  currentUser () {
    return currentUser
  },

  onAuthChange (callback) {
    authListeners.add(callback)

    // The FIRST emission waits for the saved session to be read, rather
    // than reporting null and correcting itself a moment later. Callers
    // treat the first value as "we now know who this is" and stop showing
    // a loading state on it — so an eager null means a reload bounces a
    // signed-in person out to the sign-in screen before the token has
    // even been read off disk.
    restoreSession().then(() => callback(currentUser))

    return () => authListeners.delete(callback)
  },

  async updateProfile (fields) {
    const row = await patch('/api/auth/me', fields)
    currentUser = toUser(row.id, row)
    emitAuth()
    return currentUser
  },

  async deleteAccount () {
    await del('/api/auth/me')
    return auth.signOut()
  }
}

// ---------------------------------------------------------------------------
// chats
// ---------------------------------------------------------------------------

const asChat = raw => toChat(raw.id, raw, currentUser?.id)

export const chats = {
  subscribeToList (callback) {
    let alive = true

    const load = async () => {
      try {
        await restoreSession()
        const rows = await get('/api/chats')
        if (alive) callback(rows.map(asChat))
      } catch (error) {
        if (alive) console.error('[ZIvapp] could not load chats:', error)
      }
    }

    load()

    // The server publishes to 'chats' whenever anything that shows up in the
    // list changes — a chat created, a message sent, edited, deleted, or read.
    // Refetching is one indexed query and keeps the ordering and unread counts
    // the server decided, rather than duplicating that arithmetic here.
    const off = onTopic('chats', load)

    return () => {
      alive = false
      off()
    }
  },

  subscribeToChat (chatId, callback) {
    let alive = true

    const load = async () => {
      try {
        await restoreSession()
        const row = await get(`/api/chats/${chatId}`)
        if (alive) callback(asChat(row))
      } catch (error) {
        if (alive) console.error('[ZIvapp] could not load chat:', error)
      }
    }

    load()
    const off = onTopic(`chat:${chatId}`, frame => {
      // Only membership changes alter the chat document itself; a message
      // arriving is the messages subscription's business.
      if (frame.action === 'member-left' || frame.action === 'member-joined') load()
    })

    return () => { alive = false; off() }
  },

  async get (chatId) {
    return asChat(await get(`/api/chats/${chatId}`))
  },

  async getOrCreateDirect (userId) {
    return asChat(await post('/api/chats/direct', { userId }))
  },

  async createGroup ({ name, description, memberIds }) {
    return asChat(await post('/api/chats/group', { name, description, memberIds }))
  },

  async setMuted (chatId, isMuted) {
    await patch(`/api/chats/${chatId}/state`, { isMuted })
  },

  async setPinned (chatId, isPinned) {
    await patch(`/api/chats/${chatId}/state`, { isPinned })
  },

  async setArchived (chatId, isArchived) {
    await patch(`/api/chats/${chatId}/state`, { isArchived })
  },

  async leaveGroup (chatId) {
    await post(`/api/chats/${chatId}/leave`)
  },

  async updateGroup (chatId, fields) {
    return asChat(await patch(`/api/chats/${chatId}`, fields))
  },

  async addMembers (chatId, userIds) {
    await post(`/api/chats/${chatId}/members`, { userIds })
  },

  async removeMember (chatId, userId) {
    await del(`/api/chats/${chatId}/members/${userId}`)
  },

  async setMemberRole (chatId, userId, role) {
    await patch(`/api/chats/${chatId}/members/${userId}`, { role })
  },

  // Still no routes: both destroy history, and neither has been specified
  // carefully enough to build — who may do it, and what the other
  // participants see afterwards.
  clearMessages: () => { throw notImplemented('chats.clearMessages()') },
  deleteChat:    () => { throw notImplemented('chats.deleteChat()') }
}

// ---------------------------------------------------------------------------
// messages
// ---------------------------------------------------------------------------

const asMessage = (raw, chatId) => toMessage(raw.id, raw, chatId)
const byTime = (a, b) => a.createdAt - b.createdAt

export const messages = {
  subscribe (chatId, callback) {
    let alive = true
    // The cache is what makes a new message cost one frame rather than a
    // refetch of the whole conversation.
    let cache = []

    const emit = () => { if (alive) callback([...cache]) }

    ;(async () => {
      try {
        await restoreSession()
        const rows = await get(`/api/chats/${chatId}/messages`)
        if (!alive) return
        cache = rows.map(r => asMessage(r, chatId)).sort(byTime)
        emit()
      } catch (error) {
        if (alive) console.error('[ZIvapp] could not load messages:', error)
      }
    })()

    const off = onTopic(`chat:${chatId}`, frame => {
      if (frame.action === 'message') {
        const next = asMessage(frame.message, chatId)
        // The sender already has this one from the POST response; replacing
        // rather than appending is what stops it rendering twice.
        const at = cache.findIndex(m => m.id === next.id)
        if (at === -1) cache.push(next)
        else cache[at] = next
        cache.sort(byTime)
        emit()
      } else if (frame.action === 'message-edited') {
        const next = asMessage(frame.message, chatId)
        cache = cache.map(m => (m.id === next.id ? next : m))
        emit()
      } else if (frame.action === 'message-deleted') {
        // A tombstone, not a removal: other people have it on screen and
        // replies point at it.
        cache = cache.map(m =>
          m.id === frame.messageId
            ? { ...m, body: '', mediaUrl: null, deletedAt: new Date() }
            : m
        )
        emit()
      }
    })

    return () => { alive = false; off() }
  },

  async loadOlder (chatId, before, limit = 30) {
    const params = new URLSearchParams({ limit: String(limit) })
    if (before) params.set('before', new Date(before).toISOString())
    const rows = await get(`/api/chats/${chatId}/messages?${params}`)
    return rows.map(r => asMessage(r, chatId)).sort(byTime)
  },

  async send (chatId, draft) {
    const row = await post(`/api/chats/${chatId}/messages`, {
      id:        draft.id,
      type:      draft.type ?? 'text',
      body:      draft.body ?? null,
      mediaUrl:  draft.mediaUrl ?? null,
      mediaMeta: draft.mediaMeta ?? null,
      replyToId: draft.replyToId ?? null
    })
    return asMessage(row, chatId)
  },

  async edit (chatId, messageId, body) {
    return asMessage(await patch(`/api/chats/messages/${messageId}`, { body }), chatId)
  },

  async remove (chatId, messageId) {
    await del(`/api/chats/messages/${messageId}`)
  },

  async markRead (chatId) {
    await post(`/api/chats/${chatId}/read`)
  },

  // message_reactions exists in the schema but has no routes yet.
  react:   () => { throw notImplemented('messages.react()') },
  unreact: () => { throw notImplemented('messages.unreact()') },
  // The messages table carries a FULLTEXT index for this — the thing
  // Firestore could not do at all — but nothing exposes it yet.
  search:  () => { throw notImplemented('messages.search()') }
}

// ---------------------------------------------------------------------------
// presence
// ---------------------------------------------------------------------------

export const presence = {
  startHeartbeat () {
    // The socket connection IS the heartbeat: the server marks someone online
    // when it opens and offline when the last one closes, so there is nothing
    // to poll. Returning a no-op keeps the contract's teardown shape.
    connect()
    return () => {}
  },

  watch (userIds, callback) {
    const offs = userIds.map(id =>
      onTopic(`presence:${id}`, frame =>
        callback({ userId: frame.userId, isOnline: frame.isOnline, lastSeen: new Date(frame.lastSeen) })
      )
    )
    return () => offs.forEach(off => off())
  },

  setTyping (chatId, isTyping) {
    // Never written to the database: it is worthless a second later, and
    // storing it would be the busiest query in the app for no lasting value.
    send({ action: 'typing', chatId, isTyping })
  },

  watchTyping (chatId, callback) {
    return onTopic(`typing:${chatId}`, frame =>
      callback({ userId: frame.userId, isTyping: frame.isTyping })
    )
  }
}

// ---------------------------------------------------------------------------
// contacts
// ---------------------------------------------------------------------------

export const contacts = {
  subscribe (callback) {
    let alive = true
    const load = async () => {
      try {
        await restoreSession()
        const rows = await get('/api/contacts')
        if (alive) callback(rows)
      } catch (error) {
        if (alive) console.error('[ZIvapp] could not load contacts:', error)
      }
    }
    load()
    const off = onTopic('contacts', load)
    return () => { alive = false; off() }
  },

  async findByPhone (phone) {
    try {
      const row = await get(`/api/contacts/by-phone/${encodeURIComponent(phone)}`)
      return toUser(row.id, row)
    } catch (error) {
      // Not found is an answer here, not a failure: the number simply has
      // no account, and every caller wants null rather than a throw.
      if (error?.code === ErrorCode.NOT_FOUND) return null
      throw error
    }
  },

  async discover (phones) {
    const matches = await post('/api/contacts/discover', { phones })
    return matches.map(m => ({ phone: m.phone, user: toUser(m.user.id, m.user) }))
  },

  // The contract takes one options object, and the mock already did. This
  // provider took two positional arguments, so a screen written against
  // the contract would have added a contact with no name on REST and
  // worked fine on mock — the kind of difference the whole three-provider
  // design exists to prevent.
  async add ({ userId, savedName = '' }) {
    await post('/api/contacts', { userId, savedName })
  },

  async remove (contactId) {
    await del(`/api/contacts/${contactId}`)
  },

  async block (userId) {
    await post('/api/contacts/blocked', { userId })
  },

  async unblock (userId) {
    await del(`/api/contacts/blocked/${userId}`)
  },

  subscribeToBlocked (callback) {
    let alive = true
    const load = async () => {
      try {
        await restoreSession()
        const rows = await get('/api/contacts/blocked')
        if (alive) callback(rows.map(r => toUser(r.id, r)))
      } catch (error) {
        if (alive) console.error('[ZIvapp] could not load blocked users:', error)
      }
    }
    load()
    const off = onTopic('blocked', load)
    return () => { alive = false; off() }
  }
}

// ---------------------------------------------------------------------------
// status
// ---------------------------------------------------------------------------

const asStatus = raw => ({
  ...toStatus(raw.id, raw, currentUser?.id),
  authorName: raw.authorName,
  authorAvatarUrl: raw.authorAvatarUrl,
  viewedByMe: raw.viewedByMe
})

/** Feed and mine differ only in their endpoint, so they share this. */
function subscribeToStatus (path, callback) {
  let alive = true
  const load = async () => {
    try {
      await restoreSession()
      const rows = await get(path)
      if (alive) callback(rows.map(asStatus))
    } catch (error) {
      if (alive) console.error('[ZIvapp] could not load status:', error)
    }
  }
  load()
  const off = onTopic('status', load)
  return () => { alive = false; off() }
}

export const status = {
  subscribeToFeed: callback => subscribeToStatus('/api/status', callback),
  subscribeToMine: callback => subscribeToStatus('/api/status/mine', callback),

  async post (draft) {
    return asStatus(await post('/api/status', draft))
  },

  async markViewed (statusId) {
    await post(`/api/status/${statusId}/views`)
  },

  async getViewers (statusId) {
    return get(`/api/status/${statusId}/views`)
  },

  async remove (statusId) {
    await del(`/api/status/${statusId}`)
  }
}

// ---------------------------------------------------------------------------
// channels
// ---------------------------------------------------------------------------

/**
 * Channels are the one service here that is pull-only, on purpose.
 *
 * Every other subscription re-emits what the socket frame carried. A channel
 * frame carries no content at all — `{topic:'channels', action, channelId}` —
 * because a post reaching 50,000 online followers must be 50,000 tiny nudges
 * rather than 50,000 copies of the post. So the handler refetches, and it
 * refetches only when the nudge names the channel being watched.
 *
 * See docs/CHANNELS.md. Getting this wrong is not a slow page, it is an
 * outage.
 */

const asChannel = raw => toChannel(raw.id, raw)
const asPost = raw => toChannelPost(raw.id, raw)

export const channels = {
  subscribeToMine (callback) {
    let alive = true
    const load = async () => {
      try {
        await restoreSession()
        const rows = await get('/api/channels')
        if (alive) callback(rows.map(asChannel))
      } catch (error) {
        if (alive) console.error('[ZIvapp] could not load channels:', error)
      }
    }
    load()
    // Every channel action changes this list: a new post reorders it, a
    // follow adds to it, a delete removes from it.
    const off = onTopic('channels', load)
    return () => { alive = false; off() }
  },

  subscribeToPosts (channelId, callback) {
    let alive = true
    const load = async () => {
      try {
        await restoreSession()
        const rows = await get(`/api/channels/${channelId}/posts`)
        if (alive) callback(rows.map(asPost))
      } catch (error) {
        if (alive) console.error('[ZIvapp] could not load channel posts:', error)
      }
    }
    load()

    const off = onTopic('channels', frame => {
      // The nudge names one channel. A user following thirty channels must
      // not refetch this one because a different one posted.
      if (frame?.channelId === channelId) load()
    })
    return () => { alive = false; off() }
  },

  async get (channelId) {
    await restoreSession()
    return asChannel(await get(`/api/channels/${channelId}`))
  },

  async getByHandle (handle) {
    await restoreSession()
    try {
      return asChannel(await get(`/api/channels/handle/${encodeURIComponent(handle)}`))
    } catch (error) {
      // A link to a channel that no longer exists is an ordinary outcome for
      // the screen that resolves it, not an error to propagate.
      if (error?.code === ErrorCode.NOT_FOUND) return null
      throw error
    }
  },

  async search (term = '') {
    await restoreSession()
    const rows = await get(`/api/channels/search?q=${encodeURIComponent(term)}`)
    return rows.map(asChannel)
  },

  async create (draft) {
    return asChannel(await post('/api/channels', draft))
  },

  async update (channelId, patchBody) {
    return asChannel(await patch(`/api/channels/${channelId}`, patchBody))
  },

  async follow (channelId) {
    return asChannel(await post(`/api/channels/${channelId}/follow`))
  },

  async unfollow (channelId) {
    await del(`/api/channels/${channelId}/follow`)
  },

  async setMuted (channelId, isMuted) {
    await put(`/api/channels/${channelId}/muted`, { isMuted })
  },

  async post (channelId, draft) {
    return asPost(await post(`/api/channels/${channelId}/posts`, draft))
  },

  async editPost (postId, body) {
    return asPost(await patch(`/api/channels/posts/${postId}`, { body }))
  },

  async deletePost (postId) {
    await del(`/api/channels/posts/${postId}`)
  },

  /** Returns the new tallies: emoji → count, with no user attached to any. */
  async react (postId, emoji) {
    return post(`/api/channels/posts/${postId}/reactions`, { emoji })
  },

  async markRead (channelId) {
    await post(`/api/channels/${channelId}/read`)
  },

  async delete (channelId) {
    await del(`/api/channels/${channelId}`)
  }
}

// ---------------------------------------------------------------------------
// communities
// ---------------------------------------------------------------------------

const asCommunity = raw => toCommunity(raw.id, raw)

/**
 * A community group is an ordinary Chat with two extra fields the chat
 * model does not carry: whether the caller has joined it, and whether it
 * is the announcement group. Both drive a button on the row, so they ride
 * alongside rather than being looked up again per group.
 */
const asGroup = raw => ({
  ...toChat(raw.id, raw),
  isJoined:       Boolean(raw.isJoined),
  isAnnouncement: Boolean(raw.isAnnouncement)
})

export const communities = {
  subscribeToMine (callback) {
    let alive = true
    const load = async () => {
      try {
        await restoreSession()
        const rows = await get('/api/communities')
        if (alive) callback(rows.map(asCommunity))
      } catch (error) {
        if (alive) console.error('[ZIvapp] could not load communities:', error)
      }
    }
    load()

    // Both topics, on purpose. A message in one of a community's groups
    // changes the preview shown on its row in this list, and that arrives
    // on 'chats' — subscribing only to 'communities' meant the list went
    // stale the moment anyone said anything.
    const offCommunities = onTopic('communities', load)
    const offChats = onTopic('chats', load)
    return () => { alive = false; offCommunities(); offChats() }
  },

  subscribeToGroups (communityId, callback) {
    let alive = true
    const load = async () => {
      try {
        await restoreSession()
        const rows = await get(`/api/communities/${communityId}/groups`)
        if (alive) callback(rows.map(asGroup))
      } catch (error) {
        if (!alive) return
        /*
         * A community that is gone, or that you have just left, has no
         * groups — that is an answer, not a failure. Both arrive here
         * because this subscription keeps running for the moment between
         * the delete and the screen navigating away, and logging them
         * puts a red error in the console for an entirely ordinary event.
         */
        if (error?.code === ErrorCode.NOT_FOUND || error?.code === ErrorCode.PERMISSION) {
          callback([])
          return
        }
        console.error('[ZIvapp] could not load community groups:', error)
      }
    }
    load()

    const offCommunities = onTopic('communities', frame => {
      if (!frame?.communityId || frame.communityId === communityId) load()
    })
    const offChats = onTopic('chats', load)
    return () => { alive = false; offCommunities(); offChats() }
  },

  async get (communityId) {
    await restoreSession()
    return asCommunity(await get(`/api/communities/${communityId}`))
  },

  async create (draft) {
    return asCommunity(await post('/api/communities', draft))
  },

  async update (communityId, patchBody) {
    return asCommunity(await patch(`/api/communities/${communityId}`, patchBody))
  },

  async addGroup (communityId, chatId) {
    return asGroup(await put(`/api/communities/${communityId}/groups/${chatId}`))
  },

  async createGroup (communityId, name) {
    return asGroup(await post(`/api/communities/${communityId}/groups`, { name }))
  },

  async removeGroup (communityId, chatId) {
    await del(`/api/communities/${communityId}/groups/${chatId}`)
  },

  async joinGroup (communityId, chatId) {
    return asGroup(await post(`/api/communities/${communityId}/groups/${chatId}/join`))
  },

  async members (communityId) {
    await restoreSession()
    return get(`/api/communities/${communityId}/members`)
  },

  async addMembers (communityId, userIds) {
    await post(`/api/communities/${communityId}/members`, { userIds })
  },

  async removeMember (communityId, userId) {
    await del(`/api/communities/${communityId}/members/${userId}`)
  },

  async setRole (communityId, userId, role) {
    await put(`/api/communities/${communityId}/members/${userId}/role`, { role })
  },

  async leave (communityId) {
    await post(`/api/communities/${communityId}/leave`)
  },

  async delete (communityId) {
    await del(`/api/communities/${communityId}`)
  }
}

// ---------------------------------------------------------------------------
// media
// ---------------------------------------------------------------------------

/**
 * Upload without progress reporting.
 *
 * fetch still cannot report upload progress, which is why the main path
 * uses XMLHttpRequest. This exists for environments that have no XHR at
 * all, where no progress is better than no upload.
 */
async function uploadWithFetch (file) {
  const form = new FormData()
  form.append('file', file)

  const token = getAccessToken()
  const response = await fetch(`${getConfig().apiUrl}/api/media`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form
  })

  const payload = await response.json().catch(() => null)
  if (!response.ok) {
    throw new ZivError(ErrorCode.UPLOAD_FAILED, payload?.message ?? 'The upload failed.')
  }
  return { ...payload, url: `${getConfig().apiUrl}${payload.url}` }
}

export const media = {
  /**
   * Upload one file.
   *
   * XMLHttpRequest rather than fetch, purely because fetch still cannot
   * report upload progress — and on mobile data a photo takes long enough
   * that a progress bar is the difference between waiting and force-closing
   * the app.
   */
  async upload (file, { onProgress } = {}) {
    // Browsers and React Native both provide XMLHttpRequest; Node does not,
    // and a test or a server render should not fall over on that.
    if (typeof XMLHttpRequest === 'undefined') return uploadWithFetch(file)

    return new Promise((resolve, reject) => {
      const form = new FormData()
      form.append('file', file)

      const request = new XMLHttpRequest()
      request.open('POST', `${getConfig().apiUrl}/api/media`)

      const token = getAccessToken()
      if (token) request.setRequestHeader('Authorization', `Bearer ${token}`)

      if (onProgress) {
        request.upload.addEventListener('progress', event => {
          if (event.lengthComputable) {
            onProgress(Math.round((event.loaded / event.total) * 100))
          }
        })
      }

      request.addEventListener('load', () => {
        let payload = null
        try { payload = JSON.parse(request.responseText) } catch { /* not JSON */ }

        if (request.status === 201 && payload) {
          // The server returns a path; the app needs something it can put in
          // a src attribute, which means absolute.
          resolve({ ...payload, url: `${getConfig().apiUrl}${payload.url}` })
        } else {
          reject(new ZivError(
            request.status === 413 ? ErrorCode.UPLOAD_FAILED : ErrorCode.UPLOAD_FAILED,
            payload?.message ?? 'The upload failed.'
          ))
        }
      })

      request.addEventListener('error', () =>
        reject(new ZivError(ErrorCode.NETWORK, 'No connection.')))
      request.addEventListener('abort', () =>
        reject(new ZivError(ErrorCode.UPLOAD_FAILED, 'The upload was cancelled.')))

      request.send(form)
    })
  },

  async remove (path) {
    await del(`/api/media/${path}`)
  },

  async localPreview (file) {
    // Caller revokes it; see the contract.
    return URL.createObjectURL(file)
  }
}

// ---------------------------------------------------------------------------
// Not built on the server yet
// ---------------------------------------------------------------------------

/**
 * Services with no routes behind them. Generated from the contract so this
 * provider cannot silently fall out of step with the others, and so the
 * error names the method rather than failing somewhere vague.
 */
function missingService (serviceName) {
  const service = {}
  for (const method of REQUIRED_METHODS[serviceName]) {
    const isSubscription = method.startsWith('subscribe') || method.startsWith('watch')
    service[method] = isSubscription
      ? () => {
          console.warn(
            `[ZIvapp] ${serviceName}.${method}() has no route on the server yet.`
          )
          return () => {}
        }
      : () => {
          throw new ZivError(
            ErrorCode.NOT_IMPLEMENTED,
            `${serviceName}.${method}() has no route on the server yet. ` +
            `Add it under zivapp-server/src/routes/${serviceName}.js.`
          )
        }
  }
  return service
}

export const calls = missingService('calls')

// A guard against this file drifting from the contract as methods are added.
if (SERVICE_NAMES.length !== 10) {
  console.warn('[ZIvapp] the contract changed; rest/index.js may be incomplete.')
}
